import { ClipboardCheck, FileText, Printer } from 'lucide-react'
import { Button } from '~/components/ui/button'
import type { MaintenanceEvent } from '~/api/types'
import { ResultItemButton } from './result-card-item'
import type { ResultItem } from './types'
import { formatDateTime } from './utils'

export function ResultCard({
    event,
    items,
    checklistCount,
    onSelect,
    onCopy,
    onOpenReport,
}: {
    event: MaintenanceEvent
    items: ResultItem[]
    checklistCount: number
    onSelect(itemId: string): void
    onCopy(): void
    onOpenReport(opts?: { print?: boolean }): void
}) {
    const recheckCount = items.filter((item) => item.recheck_required).length
    const keepCount = Math.max(items.length - recheckCount, 0)
    const visibleItems = items.slice(0, 4)
    const hiddenCount = Math.max(items.length - visibleItems.length, 0)
    // 분모는 전체 체크리스트 개수로 통일한다(전체 N개 중 M개 영향).
    const totalCount = Math.max(checklistCount, items.length)

    return (
        <div
            className='border-border bg-card mt-4 rounded-sm border shadow-sm'
            role='group'
            aria-label='변경 영향 분석 결과'>
            <div className='border-border bg-muted/40 border-b px-5 py-4'>
                <p className='text-muted-foreground text-xs font-bold'>
                    변경 영향 분석 결과
                </p>
                <div className='mt-2 flex flex-wrap items-end gap-2'>
                    <span className='text-foreground text-3xl leading-none font-bold'>
                        {recheckCount}
                    </span>
                    <span className='text-foreground pb-0.5 text-sm font-bold'>
                        건 재점검 필요
                    </span>
                    <span className='text-muted-foreground pb-0.5 text-sm'>
                        · 전체 {totalCount}개 중 {items.length}개 영향 ·{' '}
                        {keepCount}건 유지
                    </span>
                </div>
            </div>

            <div className='grid gap-3 p-5'>
                {items.length === 0 ? (
                    <p className='text-muted-foreground text-sm'>
                        영향받는 항목이 없습니다. 기존 관리 기준을 유지해도
                        됩니다.
                    </p>
                ) : (
                    visibleItems.map((item, index) => (
                        <ResultItemButton
                            key={`${item.item_id}-${index}`}
                            item={item}
                            onSelect={onSelect}
                        />
                    ))
                )}
                {hiddenCount > 0 ? (
                    <p className='border-border bg-muted/35 text-muted-foreground rounded-sm border px-3 py-2 text-sm'>
                        나머지 {hiddenCount}개 영향 항목은 우측 “영향받은 점검
                        항목” 목록에서 전체로 확인할 수 있습니다.
                    </p>
                ) : null}
            </div>

            <div className='border-border flex flex-wrap items-center gap-2 border-t px-5 py-4'>
                <Button size='sm' onClick={onCopy}>
                    <ClipboardCheck />
                    재점검 항목 복사
                </Button>
                <Button
                    size='sm'
                    variant='outline'
                    onClick={() => onOpenReport()}>
                    <FileText />
                    보고서 보기
                </Button>
                <Button
                    size='sm'
                    variant='outline'
                    onClick={() => onOpenReport({ print: true })}>
                    <Printer />
                    인쇄 / PDF 저장
                </Button>
            </div>

            <p className='text-muted-foreground px-5 pb-5 text-sm'>
                기준 변경일: {formatDateTime(event.createdAt)}
            </p>
        </div>
    )
}
